import { z } from 'zod';

import { nicheIds } from '@/lib/tour-operator-interest';

const nameSchema = z.string().min(2, 'Name is required').max(100);
const emailSchema = z
  .string()
  .transform((val) => val.trim())
  .refine((val) => val === '' || z.string().email().safeParse(val).success, {
    message: 'Invalid email address',
  })
  .optional()
  .or(z.literal(''));
const phoneSchema = z.string().optional().or(z.literal(''));
const districtSchema = z.string().min(2, 'District is required').max(100);

const contactValidation = (data: { email?: string; phone?: string }) => {
  const hasEmail = data.email && data.email.trim().length > 0;
  const hasPhone = data.phone && data.phone.trim().length > 0;
  return hasEmail || hasPhone;
};

// RIIP Form
export const riipFormSchema = z
  .object({
    name: nameSchema,
    designation: z.string().min(2, 'Designation is required').max(100),
    institutionType: z.enum(['school', 'college']),
    institutionName: z.string().min(2, 'Institution name is required').max(200),
    email: emailSchema,
    phone: phoneSchema,
    groupSize: z.string().optional(),
  })
  .refine(contactValidation, {
    message: 'Please provide either email or phone number',
    path: ['email'],
  });

export type RIIPFormData = z.infer<typeof riipFormSchema>;

// Merch Form
export const merchFormSchema = z
  .object({
    name: nameSchema,
    email: emailSchema,
    phone: z.string().min(10, 'Valid phone number is required'),
    quantity: z.string().refine((value) => Number.isInteger(Number(value)) && Number(value) > 0, 'Enter a quantity of at least 1'),
    eventType: z.enum(['family_functions', 'corporate_events', 'personal_gifting', 'personal_use']),
    message: z.string().max(1000).optional(),
  });

export type MerchFormData = z.infer<typeof merchFormSchema>;

// Host Form
export const hostFormSchema = z
  .object({
    name: nameSchema,
    email: emailSchema,
    phone: phoneSchema,
    district: districtSchema,
    category: z.enum(['art_craft', 'agriculture', 'food']),
  })
  .refine(contactValidation, {
    message: 'Please provide either email or phone number',
    path: ['email'],
  });

export type HostFormData = z.infer<typeof hostFormSchema>;

// Book Experience Form
export const bookExperienceFormSchema = z
  .object({
    name: nameSchema,
    email: emailSchema,
    phone: phoneSchema,
    district: districtSchema,
    datesNotDecided: z.boolean(),
    preferredDates: z.date().optional(),
    groupSize: z.string().min(1, 'Group size is required'),
    groupType: z.enum(['family', 'friends', 'solo', 'colleague']).optional(),
  })
  .refine(contactValidation, {
    message: 'Please provide either email or phone number',
    path: ['email'],
  })
  .refine(
    (data) => {
      if (!data.datesNotDecided && !data.preferredDates) {
        return false;
      }
      return true;
    },
    {
      message: 'Please select a date or mark dates as not decided',
      path: ['preferredDates'],
    }
  )
  .refine(
    (data) => {
      return !!data.groupType;
    },
    {
      message: 'Please select a group type',
      path: ['groupType'],
    }
  );

export type BookExperienceFormData = z.infer<typeof bookExperienceFormSchema>;

// Pre-book Form (landing WhatsApp enquiry)
export const preBookFormSchema = z
  .object({
    sessionType: z.enum(['private', 'shared'], {
      message: 'Please choose a session type',
    }),
    name: nameSchema,
    email: emailSchema,
    phone: phoneSchema,
    preferredDate: z.date({ message: 'Please select a preferred date' }),
    totalPersons: z
      .string()
      .min(1, 'Total persons is required')
      .refine((val) => {
        const n = Number(val)
        return Number.isInteger(n) && n >= 1 && n <= 20
      }, 'Enter a number between 1 and 20'),
    preferredSession: z.enum(['morning', 'afternoon'], {
      message: 'Please choose a preferred session',
    }),
  })
  .refine(contactValidation, {
    message: 'Please provide either email or phone number',
    path: ['email'],
  });

export type PreBookFormData = z.infer<typeof preBookFormSchema>;

// Tour operator interest (/register-interest — event QR)
export const tourOperatorInterestSchema = z.object({
  niches: z.array(z.enum(nicheIds)).min(1, 'Pick at least one'),
  nicheOther: z.string().trim().max(100).optional(),
  name: nameSchema,
  company: z.string().trim().min(2, 'Company name is required').max(150),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[\d\s-]{8,18}$/, 'Enter a valid WhatsApp number'),
  email: emailSchema,
});

export type TourOperatorInterestData = z.infer<typeof tourOperatorInterestSchema>;
